import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { createLLMOMeta, createBreadcrumbSchema, createToolSchema, createHowToSchema, createFAQSchema } from "@/lib/seo"
import { getToolBySlug } from "@/data/tools/tools-registry"
import ToolPageLayout from "@/components/tools/ToolPageLayout"
import { SubmitButton } from "@/components/tools/ToolFormField"
import { SectionHeading, Paragraph, FeatureGrid, StatGrid, TipList, ContentSection } from "@/components/tools/SeoContent"

const tool = getToolBySlug("instagram-post-formatter")!

export const Route = createFileRoute("/tools/instagram-post-formatter")({
  head: () => ({
    meta: [
      ...createLLMOMeta({ title: tool.seo.title, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-post-formatter" }),
      { name: "keywords", content: tool.seo.keywords.join(", ") },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/tools/instagram-post-formatter" }],
    scripts: [
      createToolSchema({ name: tool.name, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-post-formatter" }),
      createBreadcrumbSchema({ itemListElement: [
        { position: 1, name: "Home", item: "https://zapify.pro" },
        { position: 2, name: "Free Tools", item: "https://zapify.pro/tools" },
        { position: 3, name: tool.shortName, item: "https://zapify.pro/tools/instagram-post-formatter" },
      ]}),
      createHowToSchema({ name: "How to Format Instagram Captions", description: tool.seo.description, steps: tool.howToSteps }),
      createFAQSchema(tool.faq),
    ],
  }),
  component: PostFormatterPage,
})

const INSTAGRAM_CHAR_LIMIT = 2200

function formatCaption(text: string): string {
  return text
    .split("\n")
    .map((line) => line.trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .split("\n")
    .map((line) => (line === "" ? "\u200B" : line))
    .join("\n")
}

function PostFormatterPage() {
  const [input, setInput] = useState("")
  const [output, setOutput] = useState("")
  const [copied, setCopied] = useState(false)

  const charCount = input.length
  const remaining = INSTAGRAM_CHAR_LIMIT - charCount

  const remainingColor =
    remaining > 500
      ? "text-green-600 dark:text-green-400"
      : remaining > 100
        ? "text-yellow-600 dark:text-yellow-400"
        : "text-red-600 dark:text-red-400"

  const handleFormat = () => {
    if (!input.trim()) return
    setOutput(formatCaption(input))
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(output)
    } catch {
      const textarea = document.createElement("textarea")
      textarea.value = output
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand("copy")
      document.body.removeChild(textarea)
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const reset = () => {
    setInput("")
    setOutput("")
    setCopied(false)
  }

  return (
    <ToolPageLayout
      tool={tool}
      content={
        <div className="space-y-10">
          <ContentSection>
            <SectionHeading>Why Instagram Breaks Your Caption Formatting</SectionHeading>
            <Paragraph className="mb-4">
              You spend ten minutes crafting the perfect caption in your notes app. The spacing is just right, the paragraphs breathe, and the call-to-action sits on its own line for maximum impact. Then you paste it into Instagram, hit publish, and watch in frustration as your carefully structured text collapses into an unreadable wall of words. This is one of the most common complaints among Instagram creators, and it happens because of how Instagram's backend processes text input.
            </Paragraph>
            <Paragraph className="mb-4">
              Instagram's servers apply several text-processing rules the moment you submit a caption. First, trailing whitespace is stripped from every line. Any spaces or tabs you placed at the end of a line to create visual padding are silently removed. Second, consecutive blank lines are collapsed. If you inserted two or three empty lines between paragraphs to create breathing room, Instagram reduces them to a single line break, eliminating the spacing you intended. Third, leading whitespace on lines is trimmed, which means indentation tricks do not work either.
            </Paragraph>
            <Paragraph className="mb-4">
              This behavior is consistent across both iOS and Android versions of the app, so switching devices will not help. It is particularly frustrating for creators who draft captions in external apps like Apple Notes, Google Keep, or Notion, where the formatting looks exactly right before pasting. The formatting looks perfect in the notes app because those apps respect whitespace as-is. Instagram does not, and there is no setting to change this behavior.
            </Paragraph>
            <Paragraph>
              The problem gets worse with longer captions. A 200-word educational post or a storytelling caption that spans multiple paragraphs becomes nearly impossible to read when all the paragraph breaks disappear. Followers see a dense block of text, and most of them simply scroll past rather than making the effort to parse it. This directly impacts engagement metrics because users who do not read your caption are far less likely to like, comment, or save the post. Formatting is not just about aesthetics; it is about whether your message actually gets consumed.
            </Paragraph>
          </ContentSection>
          <ContentSection>
            <SectionHeading>What This Formatter Does</SectionHeading>
            <Paragraph className="mb-4">
              This tool solves the Instagram formatting problem by processing your caption text before you paste it into the app. It applies three transformations that work together to preserve your intended layout, no matter how Instagram's servers handle the input.
            </Paragraph>
            <FeatureGrid columns={3} items={[
              { title: "Trims whitespace from every line", description: "Leading and trailing spaces are removed from each line. This ensures your caption starts clean and avoids inconsistencies caused by copy-pasting from different text editors that may add hidden spaces." },
              { title: "Normalizes excessive blank lines", description: "Three or more consecutive blank lines are collapsed into exactly two. This prevents accidental over-spacing while keeping your intentional paragraph breaks intact and consistent throughout the caption." },
              { title: "Inserts invisible characters to preserve spacing", description: "A zero-width space character (U+200B) is placed on every empty line. This invisible character is undetectable to readers but tells Instagram's parser that the line contains content, preventing it from collapsing the line break. The result is that your paragraph spacing survives exactly as you designed it." },
            ]} />
            <Paragraph className="mt-4">
              The before-and-after difference is significant. Without formatting, a three-paragraph caption with line breaks between sections becomes a single continuous block of text. With formatting, each paragraph stands on its own with clear visual separation, making the caption dramatically easier to read on mobile screens where most Instagram browsing happens.
            </Paragraph>
          </ContentSection>
          <ContentSection>
            <SectionHeading>Instagram Caption Best Practices</SectionHeading>
            <TipList tips={[
              { title: "Start with a hook.", description: "The first line of your caption is the only thing most users see in their feed before tapping \"more.\" Open with a question, a bold statement, or an intriguing claim that compels people to expand the full text. Treat your opening line like a headline." },
              { title: "Use short paragraphs.", description: "On a phone screen, even three sentences can look like a dense block of text. Break your caption into bite-sized paragraphs of one to two sentences each. Generous whitespace between sections makes your content feel lighter and more inviting to read." },
              { title: "Place strategic line breaks before your CTA.", description: "Your call-to-action should never be buried in the middle of a paragraph. Separate it with a blank line above so it visually stands on its own. This small change can measurably increase comment rates, saves, and link clicks." },
              { title: "Keep the first 125 characters compelling.", description: "Instagram truncates captions in the feed after roughly 125 characters. Everything after that is hidden behind a \"more\" button. Front-load your most interesting or valuable information so even users who never expand the caption still absorb your core message." },
              { title: "End with a question or call-to-action.", description: "Captions that close with a direct question or instruction see consistently higher engagement. Ask followers to share their opinion, tag a friend, or save the post for later. Give people a specific reason to interact rather than just passively consuming." },
              { title: "Optimize hashtag placement.", description: "Place hashtags at the very bottom of your caption separated by several line breaks, or move them to the first comment entirely. This keeps your caption body clean and readable while still benefiting from hashtag discoverability." },
            ]} />
          </ContentSection>
          <ContentSection>
            <SectionHeading>Character Limits and Formatting Rules</SectionHeading>
            <Paragraph className="mb-4">
              Understanding Instagram's character limits helps you plan your content strategy and avoid unpleasant surprises when publishing. Here are the key numbers every creator should know.
            </Paragraph>
            <StatGrid stats={[
              { value: "2,200", label: "Captions", sublabel: "Hard character limit" },
              { value: "150", label: "Bio", sublabel: "Character limit" },
              { value: "2,200", label: "Comments", sublabel: "Same as captions" },
              { value: "~125", label: "Feed Preview", sublabel: "Before \"more\" button" },
              { value: "3-5", label: "Hashtags", sublabel: "Recommended (30 max)" },
            ]} />
          </ContentSection>
          <ContentSection>
            <SectionHeading>Formatting Patterns That Drive Engagement</SectionHeading>
            <Paragraph className="mb-4">
              Great formatting is not just about looking clean. The structure of your caption influences how people read, react to, and engage with your content. Here are proven formatting patterns used by top-performing Instagram accounts.
            </Paragraph>
            <FeatureGrid items={[
              { title: "The Hook-Story-CTA Framework", description: "This is the most versatile caption structure on Instagram. Open with a one-line hook that stops the scroll, follow with a story or explanation that delivers value, and close with a clear call-to-action. Separate each section with a blank line. This pattern works for personal brands, product launches, educational content, and behind-the-scenes posts alike." },
              { title: "Numbered Lists", description: "Posts that present information as a numbered list consistently receive higher save rates because users bookmark them for future reference. Format each item on its own line with a number prefix, and add a blank line between items for readability. Lists work especially well for tips, mistakes to avoid, tools recommendations, and step-by-step guides." },
              { title: "Quote-Then-Commentary", description: "Start your caption with a powerful quote, testimonial, or customer feedback, then follow with your own commentary or lesson learned. The quote grabs attention because it reads differently from typical captions, and your follow-up provides the context and value that keeps people engaged. This format is particularly effective for coaches, consultants, and service-based businesses." },
              { title: "Problem-Solution Format", description: "Open by describing a pain point your audience relates to, then pivot to the solution. This two-part structure creates an emotional arc: the reader nods along during the problem section and leans in during the solution. End with a CTA that invites them to take the next step, whether that is visiting a link, commenting their experience, or saving the post for later. The problem-solution format is one of the highest-converting caption structures for product and service promotions." },
            ]} />
          </ContentSection>
        </div>
      }
    >
      <div className="max-w-5xl mx-auto">
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Input side */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                Your Caption
              </label>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type or paste your Instagram caption here"
                rows={10}
                className="w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 text-neutral-900 dark:text-white text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-button/40 focus:border-brand-button/40 transition-all resize-none"
              />
              <div className="flex items-center justify-between text-xs">
                <span className="text-neutral-400 dark:text-neutral-500">
                  {charCount.toLocaleString()} / {INSTAGRAM_CHAR_LIMIT.toLocaleString()} characters
                </span>
                <span className={remainingColor}>
                  {remaining.toLocaleString()} remaining
                </span>
              </div>
              <SubmitButton onClick={handleFormat} disabled={!input.trim()}>
                Format Caption
              </SubmitButton>
            </div>

            {/* Output side */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                Formatted Output
              </label>
              <textarea
                value={output}
                readOnly
                placeholder="Formatted caption will appear here"
                rows={10}
                className="w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 text-neutral-900 dark:text-white text-sm placeholder:text-neutral-400 focus:outline-none transition-all resize-none"
              />
              <div className="flex gap-3">
                <button
                  onClick={copyToClipboard}
                  disabled={!output}
                  className={`flex-1 h-12 rounded-xl font-semibold text-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
                    copied
                      ? "bg-green-500 text-white"
                      : "bg-brand-accent text-white shadow-sm hover:shadow-md hover:-translate-y-0.5"
                  }`}
                >
                  {copied ? "Copied!" : "Copy Formatted Text"}
                </button>
                <button
                  onClick={reset}
                  className="flex-1 h-12 rounded-xl border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 font-semibold text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ToolPageLayout>
  )
}
